import { useRouter } from 'next/router';

import { Alert, Anchor } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import accessUtils from '../../../utils/accessUtils';

import { emitLearnMoreEvent, EVENT_IDS } from '../../../analytics/events/singleFamilyTotalMonthlyPrice';
import { useAuth } from '../../AuthProvider';

import type { SystemStyleObject } from '@/styled-system/types';

const MonthlyRentFeeAlert = ({
  listingAlias: listingAliasProp,
  css,
}: {
  listingAlias?: string;
  css?: SystemStyleObject;
}) => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = listingAliasProp ?? (router.query.listingAlias as string);
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false,
  });

  if (!canAccessTotalMonthlyPriceWithFee) {
    return null;
  }

  return (
    <Alert data-testid="MonthlyRentFeeAlert" css={css}>
      You&apos;ll have the opportunity later to itemize any fees for this listing.{' '}
      <Anchor
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.zillow.com/rentals-network/pricing-transparency/"
        aria-label="Learn more about fee transparency regulations"
        onClick={() => {
          if (!user || !listingDetails) return;
          emitLearnMoreEvent({
            user,
            currentProperty: listingDetails,
            triggerObject: 'monthly_rent_and_security_deposit',
            triggerLocation: !listingDetails?.modularListingProgress.step?.includes('review')
              ? 'posting_path'
              : 'posting_path_review',
          });
        }}
        data-eventid={EVENT_IDS.LEARN_MORE}
      >
        Learn more
      </Anchor>{' '}
      about fee transparency regulations in your area.
    </Alert>
  );
};

export default MonthlyRentFeeAlert;
